import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useData } from '../context/DataContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const DistribucionFacturacionLine = ({ selectedCategories = [], selectedIndustries }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { getBillingDistribution, dataReady } = useData();

  // Get data from Excel (3 years)
  const excelData = dataReady ? getBillingDistribution() : { labels: [], data2022: [], data2023: [], data2024: [] };

  // Use Excel data or fallback
  const allLabels = excelData.labels.length > 0
    ? excelData.labels
    : ['Hasta 4 mdp', 'Más de 4 mdp y hasta 100 mdp', 'Más de 100 mdp y hasta 250 mdp', 'Más de 250 mdp y hasta 1,000 mdp', 'Más de 1,000 mdp y hasta 10,000 mdp', 'Más de 10,000 mdp'];

  const data2022 = excelData.data2022.length > 0 ? excelData.data2022 : [6, 21, 10, 63, 0, 0];
  const data2023 = excelData.data2023.length > 0 ? excelData.data2023 : [6, 8, 12, 14, 24, 36];
  const data2024 = excelData.data2024.length > 0 ? excelData.data2024 : [6, 13, 11, 20, 24, 26];

  const labels = selectedCategories.length > 0
    ? allLabels.filter((label) => selectedCategories.includes(label))
    : allLabels;

  // Map selected labels to their data values
  const getValuesForLabels = (allValues) => labels.map((label) => {
    const index = allLabels.indexOf(label);
    return index !== -1 ? allValues[index] : 0;
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: '2023',
        data: getValuesForLabels(data2022),
        backgroundColor: '#A2AEBB',
      },
      {
        label: '2024',
        data: getValuesForLabels(data2023),
        backgroundColor: '#476A8E',
      },
      {
        label: '2025',
        data: getValuesForLabels(data2024),
        backgroundColor: '#1F3D58',
      },
    ],
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 40,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 12, weight: 'bold' },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}%`,
        },
      },
      datalabels: {
        color: '#000',
        font: { weight: 'bold', size: 11 },
        anchor: 'end',
        align: 'end',
        offset: 0,
        formatter: (value) => `${Math.round(value)}%`,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Porcentaje (%)',
          font: { size: 12 },
        },
        ticks: { stepSize: 10, font: { size: 10 } },
      },
      y: {
        title: {
          display: true,
          text: 'Categoría de Facturación',
          font: { size: 12 },
        },
        ticks: {
          font: { size: 11 },
        },
      },
    },
  };

  const infoContent = {
    formula: 'Porcentaje = Empresas en rango / Total de empresas × 100',
    description: 'La gráfica muestra la distribución porcentual de empresas por categorías de facturación anual.',
  };

  return (
    <div style={{ maxWidth: '800px', height: '500px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={-3}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <div style={{ width: '100%', height: '550px' }}>
        <Bar data={chartData} options={options} />
      </div>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DistribucionFacturacionLine;
